import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Download,
  Plus,
  Trash2,
  Save,
  ClipboardList,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";

export const Route = createFileRoute("/education/research/worksheet")({
  head: () => ({
    meta: [
      {
        title:
          "Legal Research Worksheet — Facts, Issues, Authorities, Quotations | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "A guided worksheet that collects your facts, legal issues, authorities, and key quotations before you draft. Autosaves in the browser and exports to PDF.",
      },
      { property: "og:title", content: "Legal Research Worksheet" },
      {
        property: "og:description",
        content:
          "Map facts, issues, authorities, and quotations before writing. Autosaves and exports to PDF.",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://nkadimengdannylcs.com/education/research/worksheet",
      },
    ],
  }),
  component: WorksheetPage,
});

type Authority = { citation: string; proposition: string };
type Quotation = { source: string; text: string; pinpoint: string };

type Worksheet = {
  topic: string;
  jurisdiction: string;
  researchQuestion: string;
  scope: string;
  facts: string;
  issues: string[];
  keywords: string;
  booleanString: string;
  databases: string;
  authorities: Authority[];
  quotations: Quotation[];
  credibility: string;
  outline: string;
};

const EMPTY: Worksheet = {
  topic: "",
  jurisdiction: "South Africa",
  researchQuestion: "",
  scope: "",
  facts: "",
  issues: [""],
  keywords: "",
  booleanString: "",
  databases:
    "SAFLII, JutaStat, LexisNexis, HeinOnline, Google Scholar, gov.za",
  authorities: [{ citation: "", proposition: "" }],
  quotations: [{ source: "", text: "", pinpoint: "" }],
  credibility: "",
  outline: "",
};

const STORAGE_KEY = "ndlcs.research.worksheet.v1";

function WorksheetPage() {
  const [ws, setWs] = useState<Worksheet>(EMPTY);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setWs({ ...EMPTY, ...JSON.parse(saved) });
    } catch {
      /* ignore */
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ws));
    } catch {
      /* ignore */
    }
  }, [ws, loaded]);

  const progress = useMemo(() => {
    const filled = [
      ws.topic,
      ws.researchQuestion,
      ws.facts,
      ws.issues.some((i) => i.trim()),
      ws.authorities.some((a) => a.citation.trim()),
      ws.quotations.some((q) => q.text.trim()),
      ws.outline,
    ].filter(Boolean).length;
    return Math.round((filled / 7) * 100);
  }, [ws]);

  function handleDownload() {
    if (!ws.topic.trim()) {
      toast.error("Add a topic before exporting.");
      return;
    }
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    doc.setFont("times", "normal");
    const MARGIN = 20;
    const WIDTH = 210 - MARGIN * 2;
    let y = MARGIN;
    const ensure = (n: number) => {
      if (y + n > 297 - MARGIN) {
        doc.addPage();
        y = MARGIN;
      }
    };
    const h1 = (t: string) => {
      doc.setFont("times", "bold");
      doc.setFontSize(16);
      ensure(9);
      doc.text(t, MARGIN, y);
      y += 8;
      doc.setFont("times", "normal");
      doc.setFontSize(11);
    };
    const h2 = (t: string) => {
      doc.setFont("times", "bold");
      doc.setFontSize(12);
      ensure(7);
      y += 2;
      doc.text(t, MARGIN, y);
      y += 6;
      doc.setFont("times", "normal");
      doc.setFontSize(11);
    };
    const p = (t: string) => {
      if (!t?.trim()) {
        doc.setFont("times", "italic");
        doc.text("—", MARGIN, y);
        doc.setFont("times", "normal");
        y += 6;
        return;
      }
      const lines = doc.splitTextToSize(t.trim(), WIDTH);
      lines.forEach((l: string) => {
        ensure(5.5);
        doc.text(l, MARGIN, y);
        y += 5.5;
      });
    };

    h1(`Legal Research Worksheet — ${ws.topic}`);
    doc.setFontSize(10);
    doc.setFont("times", "italic");
    p(`Jurisdiction: ${ws.jurisdiction || "—"}`);
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    y += 2;

    h2("Research question");
    p(ws.researchQuestion);
    h2("Scope");
    p(ws.scope);
    h2("Facts");
    p(ws.facts);

    h2("Legal issues");
    ws.issues.forEach((iss, i) => p(`${i + 1}. ${iss || "—"}`));

    h2("Search strategy");
    p(`Keywords: ${ws.keywords || "—"}`);
    p(`Boolean string: ${ws.booleanString || "—"}`);
    p(`Databases: ${ws.databases || "—"}`);

    h2("Authorities");
    ws.authorities.forEach((a, i) => {
      p(`${i + 1}. ${a.citation || "—"}`);
      if (a.proposition?.trim()) p(`   Proposition: ${a.proposition}`);
    });

    h2("Key quotations");
    ws.quotations.forEach((q, i) => {
      p(`${i + 1}. Source: ${q.source || "—"}${q.pinpoint ? ` (${q.pinpoint})` : ""}`);
      if (q.text?.trim()) p(`   "${q.text}"`);
    });

    h2("Credibility notes");
    p(ws.credibility);

    h2("Outline");
    p(ws.outline);

    const filename =
      "research-worksheet-" +
      ws.topic.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") +
      ".pdf";
    doc.save(filename);
    toast.success("Worksheet PDF downloaded.");
  }

  return (
    <div className="container-narrow py-10 md:py-14">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/education/research"
            className="inline-flex items-center gap-1 text-sm text-navy hover:underline"
          >
            <ArrowLeft className="size-4" /> Back to research hub
          </Link>
          <h1 className="mt-2 font-display text-3xl text-navy md:text-4xl">
            Guided legal research worksheet
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Collect facts, issues, authorities, and key quotations{" "}
            <strong>before</strong> you start writing. Autosaves locally,
            exports to PDF.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-white hover:bg-navy/90"
        >
          <Download className="size-4" /> Export worksheet PDF
        </button>
      </div>

      <div className="mb-6 rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-2 text-navy">
            <ClipboardList className="size-4" /> Worksheet completion
          </span>
          <span>{progress}%</span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full bg-gold transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <Group title="1. Topic & question">
        <div className="grid gap-3 md:grid-cols-2">
          <Field
            label="Topic"
            value={ws.topic}
            onChange={(v) => setWs({ ...ws, topic: v })}
          />
          <Field
            label="Jurisdiction"
            value={ws.jurisdiction}
            onChange={(v) => setWs({ ...ws, jurisdiction: v })}
          />
        </div>
        <Textarea
          label="Research question (narrow it to something answerable in 2,000–3,000 words)"
          value={ws.researchQuestion}
          onChange={(v) => setWs({ ...ws, researchQuestion: v })}
        />
        <Textarea
          label="Scope — what you WILL and WILL NOT cover"
          value={ws.scope}
          onChange={(v) => setWs({ ...ws, scope: v })}
        />
      </Group>

      <Group title="2. Facts">
        <Textarea
          label="Material facts (chronology, parties, key events)"
          rows={6}
          value={ws.facts}
          onChange={(v) => setWs({ ...ws, facts: v })}
        />
      </Group>

      <Group title="3. Legal issues">
        <StringList
          items={ws.issues}
          onChange={(items) => setWs({ ...ws, issues: items })}
          placeholder="State each issue as a precise legal question."
        />
      </Group>

      <Group title="4. Search strategy" icon={<Search className="size-4" />}>
        <Textarea
          label="Keywords (legal terms, synonyms, statutes)"
          value={ws.keywords}
          onChange={(v) => setWs({ ...ws, keywords: v })}
        />
        <Textarea
          label='Boolean search string (e.g. "human rights" AND privacy AND (data OR technology) NOT trademark)'
          value={ws.booleanString}
          onChange={(v) => setWs({ ...ws, booleanString: v })}
        />
        <Textarea
          label="Databases you'll search"
          value={ws.databases}
          onChange={(v) => setWs({ ...ws, databases: v })}
        />
      </Group>

      <Group title="5. Authorities">
        <p className="mb-2 text-xs text-muted-foreground">
          Every proposition must be supported by an authority. Add cases,
          legislation, journal articles, books. Verify each at the primary
          source before submitting.
        </p>
        {ws.authorities.map((a, i) => (
          <div
            key={i}
            className="mb-2 rounded-md border border-border bg-card p-3"
          >
            <div className="flex items-center gap-2">
              <span className="w-6 text-xs font-semibold text-navy">
                {i + 1}.
              </span>
              <input
                type="text"
                aria-label={`Authority ${i + 1} citation`}
                placeholder="Full citation"
                value={a.citation}
                onChange={(e) =>
                  setWs({
                    ...ws,
                    authorities: ws.authorities.map((x, idx) =>
                      idx === i ? { ...x, citation: e.target.value } : x,
                    ),
                  })
                }
                className="flex-1 rounded-md border border-input bg-background p-2 text-sm"
              />
              <button
                type="button"
                aria-label={`Remove authority ${i + 1}`}
                onClick={() =>
                  setWs({
                    ...ws,
                    authorities: ws.authorities.filter((_, idx) => idx !== i),
                  })
                }
                className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
            <textarea
              aria-label={`Authority ${i + 1} proposition`}
              placeholder="Proposition this authority supports."
              value={a.proposition}
              rows={2}
              onChange={(e) =>
                setWs({
                  ...ws,
                  authorities: ws.authorities.map((x, idx) =>
                    idx === i ? { ...x, proposition: e.target.value } : x,
                  ),
                })
              }
              className="mt-2 w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setWs({
              ...ws,
              authorities: [...ws.authorities, { citation: "", proposition: "" }],
            })
          }
          className="inline-flex items-center gap-2 rounded-md border border-dashed border-input px-3 py-1.5 text-xs text-navy hover:bg-accent"
        >
          <Plus className="size-3.5" /> Add authority
        </button>
      </Group>

      <Group title="6. Key quotations">
        <p className="mb-2 text-xs text-muted-foreground">
          Copy short direct quotes with source & pinpoint. Never paraphrase
          without noting it here first.
        </p>
        {ws.quotations.map((q, i) => (
          <div
            key={i}
            className="mb-2 rounded-md border border-border bg-card p-3"
          >
            <div className="flex items-center gap-2">
              <span className="w-6 text-xs font-semibold text-navy">
                {i + 1}.
              </span>
              <input
                type="text"
                aria-label={`Quotation ${i + 1} source`}
                placeholder="Source (case / article / statute)"
                value={q.source}
                onChange={(e) =>
                  setWs({
                    ...ws,
                    quotations: ws.quotations.map((x, idx) =>
                      idx === i ? { ...x, source: e.target.value } : x,
                    ),
                  })
                }
                className="flex-1 rounded-md border border-input bg-background p-2 text-sm"
              />
              <input
                type="text"
                aria-label={`Quotation ${i + 1} pinpoint`}
                placeholder="Pinpoint (para / page)"
                value={q.pinpoint}
                onChange={(e) =>
                  setWs({
                    ...ws,
                    quotations: ws.quotations.map((x, idx) =>
                      idx === i ? { ...x, pinpoint: e.target.value } : x,
                    ),
                  })
                }
                className="w-32 rounded-md border border-input bg-background p-2 text-sm"
              />
              <button
                type="button"
                aria-label={`Remove quotation ${i + 1}`}
                onClick={() =>
                  setWs({
                    ...ws,
                    quotations: ws.quotations.filter((_, idx) => idx !== i),
                  })
                }
                className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
            <textarea
              aria-label={`Quotation ${i + 1} text`}
              placeholder='"Direct quotation, verbatim."'
              value={q.text}
              rows={2}
              onChange={(e) =>
                setWs({
                  ...ws,
                  quotations: ws.quotations.map((x, idx) =>
                    idx === i ? { ...x, text: e.target.value } : x,
                  ),
                })
              }
              className="mt-2 w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setWs({
              ...ws,
              quotations: [
                ...ws.quotations,
                { source: "", text: "", pinpoint: "" },
              ],
            })
          }
          className="inline-flex items-center gap-2 rounded-md border border-dashed border-input px-3 py-1.5 text-xs text-navy hover:bg-accent"
        >
          <Plus className="size-3.5" /> Add quotation
        </button>
      </Group>

      <Group title="7. Credibility check">
        <Textarea
          label="Are your sources authoritative, accurate, current, relevant? Note any that need verifying."
          rows={4}
          value={ws.credibility}
          onChange={(v) => setWs({ ...ws, credibility: v })}
        />
      </Group>

      <Group title="8. Outline">
        <Textarea
          label="Logical structure before you start drafting"
          rows={6}
          value={ws.outline}
          onChange={(v) => setWs({ ...ws, outline: v })}
        />
      </Group>

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-white hover:bg-navy/90"
        >
          <Download className="size-4" /> Export worksheet PDF
        </button>
        <Link
          to="/education/writing/article-editor"
          className="inline-flex items-center gap-2 rounded-md border border-input px-4 py-2 text-sm text-navy hover:bg-accent"
        >
          Start drafting the article →
        </Link>
        <button
          type="button"
          onClick={() => {
            setWs(EMPTY);
            toast.success("Worksheet cleared.");
          }}
          className="inline-flex items-center gap-2 rounded-md border border-input px-4 py-2 text-sm text-navy hover:bg-accent"
        >
          <Trash2 className="size-4" /> Clear
        </button>
        <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Save className="size-3.5" /> Autosaved locally.
        </span>
      </div>
    </div>
  );
}

function Group({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6 rounded-lg border border-border bg-card p-5">
      <h2 className="mb-3 inline-flex items-center gap-2 font-display text-lg text-navy">
        {icon} {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-navy">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
      />
    </label>
  );
}

function Textarea({
  label,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-navy">
        {label}
      </span>
      <textarea
        value={value}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
      />
    </label>
  );
}

function StringList({
  items,
  onChange,
  placeholder,
}: {
  items: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="mt-2 w-6 text-right text-xs font-semibold text-navy">
            {i + 1}.
          </span>
          <textarea
            value={v}
            rows={2}
            placeholder={placeholder}
            onChange={(e) =>
              onChange(items.map((it, idx) => (idx === i ? e.target.value : it)))
            }
            className="flex-1 rounded-md border border-input bg-background p-2 text-sm"
          />
          <button
            type="button"
            aria-label={`Remove item ${i + 1}`}
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
            className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, ""])}
        className="inline-flex items-center gap-2 rounded-md border border-dashed border-input px-3 py-1.5 text-xs text-navy hover:bg-accent"
      >
        <Plus className="size-3.5" /> Add
      </button>
    </div>
  );
}
